import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, FileText, Image as ImageIcon, Newspaper, Users2, MapPin, ArrowRight } from "lucide-react";
import api, { API } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ShareButtons } from "@/components/ShareButtons";

const GALLERY = [
  "https://images.unsplash.com/photo-1604212563354-546134b8004f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxOaWdlcmlhJTIwY29tbXVuaXR5JTIwcmFsbHklMjBzdXBwb3J0ZXJzfGVufDB8fHx8MTc4NjA5MDM0MHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1604212561903-5ca7f041c58b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxOaWdlcmlhJTIwY29tbXVuaXR5JTIwcmFsbHklMjBzdXBwb3J0ZXJzfGVufDB8fHx8MTc4NjA5MDM0MHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1633116937591-f312921e99b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxOaWdlcmlhJTIwY29tbXVuaXR5JTIwcmFsbHklMjBzdXBwb3J0ZXJzfGVufDB8fHx8MTc4NjA5MDM0MHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1603279760408-ba34e1b9857c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxOaWdlcmlhJTIwY29tbXVuaXR5JTIwcmFsbHklMjBzdXBwb3J0ZXJzfGVufDB8fHx8MTc4NjA5MDM0MHww&ixlib=rb-4.1.0&q=85",
];

const KIT = [
  { title: "Campaign Poster (A2)", type: "Poster", url: GALLERY[0] },
  { title: "WhatsApp Status Banner", type: "Banner", url: GALLERY[2] },
  { title: "Rally Announcement DP", type: "Profile Picture", url: GALLERY[3] },
];

export default function Media() {
  const [press, setPress] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [gallery, setGallery] = useState([]);
  useEffect(() => {
    api.get("/content/press").then((r) => setPress(r.data)).catch(() => {});
    api.get("/feeds").then((r) => setFeeds(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <section className="topo-bg text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <span className="eyebrow text-secondary">Media & Press</span>
          <h1 className="font-heading font-black text-4xl md:text-5xl mt-3">Official Statements & Campaign Assets</h1>
          <p className="mt-4 text-lg text-emerald-50 max-w-2xl">
            The single source of truth for the movement. Verify every statement here before sharing — beware of fake news and misquotes.
          </p>
        </div>
      </section>

      {/* GRASSROOTS FEED */}
      {feeds.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pt-20">
          <div className="flex items-center gap-2 mb-8"><Users2 className="w-6 h-6 text-primary" /><h2 className="font-heading font-extrabold text-3xl">Grassroots Feed</h2></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {feeds.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                data-testid={`public-feed-${p.id}`} className="bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm group">
                {p.image_path && <div className="aspect-video overflow-hidden"><img src={`${API}/files/${p.image_path}`} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                <div className="p-5">
                  <div className="flex items-center gap-1.5 text-xs text-primary font-bold"><MapPin className="w-3.5 h-3.5" /> {p.lga} · {(p.created_at || "").slice(0, 10)}</div>
                  <h3 className="font-heading font-bold text-lg mt-1.5">{p.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.body}</p>
                  <div className="text-xs text-slate-400 mt-3">— {p.author}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* PRESS RELEASES */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <div className="flex items-center gap-2 mb-8"><Newspaper className="w-6 h-6 text-primary" /><h2 className="font-heading font-extrabold text-3xl">Press Release Archive</h2></div>
        <Accordion type="single" collapsible className="space-y-3">
          {press.map((p, i) => (
            <AccordionItem key={i} value={`p${i}`} data-testid={`press-${i}`} className="bg-white border border-slate-100 rounded-2xl px-5 shadow-sm">
              <AccordionTrigger className="hover:no-underline text-left">
                <div>
                  <div className="font-heading font-bold text-base md:text-lg">{p.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">{p.author} · {p.date}</div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {p.image_path && <img src={`${API}/files/${p.image_path}`} alt={p.title} className="w-full rounded-xl mb-4 max-h-80 object-cover" />}
                <p className="whitespace-pre-line">{p.body}</p>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
                  <Link to={`/news/${p.id}`} data-testid={`press-read-${p.id}`} className="inline-flex items-center gap-1.5 text-primary font-bold text-sm hover:gap-2.5 transition-all">
                    Read full statement <ArrowRight className="w-4 h-4" />
                  </Link>
                  <ShareButtons url={`${API}/news/${p.id}/share`} title={p.title} testid={`press-share-${p.id}`} />
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* GALLERY */}
      <section className="bg-muted/50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-8"><ImageIcon className="w-6 h-6 text-primary" /><h2 className="font-heading font-extrabold text-3xl">Photo Gallery</h2></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {gallery.map((g, i) => (
              <motion.div key={g.id || i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                data-testid={`gallery-item-${g.id || i}`}
                className="aspect-square rounded-2xl overflow-hidden shadow-sm group relative">
                <img src={g.image_url || `${API}/files/${g.image_path}`} alt={g.caption || "Gallery"} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                {g.caption && <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white text-xs p-2 font-medium">{g.caption}</div>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DOWNLOAD KIT */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-center gap-2 mb-8"><FileText className="w-6 h-6 text-primary" /><h2 className="font-heading font-extrabold text-3xl">Campaign Download Kit</h2></div>
        <div className="grid md:grid-cols-3 gap-6">
          {KIT.map((k, i) => (
            <div key={i} data-testid={`kit-${i}`} className="bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm group">
              <div className="aspect-video overflow-hidden"><img src={k.url} alt={k.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="p-5">
                <div className="text-xs uppercase text-primary font-bold">{k.type}</div>
                <h3 className="font-heading font-bold text-lg mt-1">{k.title}</h3>
                <a href={k.url} download target="_blank" rel="noopener noreferrer">
                  <Button className="w-full mt-4 rounded-full bg-primary hover:bg-primary/90 font-bold gap-2" data-testid={`kit-download-${i}`}><Download className="w-4 h-4" /> Download</Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
